/**
 * Universal Icon Component
 *
 * Type-safe wrapper for lucide-react icons using semantic path notation.
 * Enforces AGENTS.md requirement: NEVER import lucide-react directly.
 *
 * @example
 * <Icon name="status.success" className="text-green-500" />
 * <Icon name="actions.rocket" size="xl" />
 * <Icon name="status.loading" className="animate-spin" />
 */


import { cn } from '@/lib/utils'
import { getIcon, ICON_SIZES, type NestedIconPath, type IconSize } from '@/config/icons'

interface IconProps {
  readonly name: NestedIconPath
  readonly size?: IconSize
  readonly className?: string
  readonly 'aria-hidden'?: boolean
  readonly 'aria-label'?: string
}

/**
 * Icon component with semantic path-based access
 *
 * Benefits:
 * - Type-safe icon names (compile-time checking)
 * - Semantic categorization (status, actions, business, etc.)
 * - Consistent sizing via predefined options
 * - WCAG-compliant with aria-label support
 */
export function Icon({
  name,
  size = 'md',
  className,
  'aria-hidden': ariaHidden = true,
  'aria-label': ariaLabel,
}: IconProps) {
  const IconComponent = getIcon(name)
  const sizeClass = ICON_SIZES[size]

  return (
    <IconComponent
      className={cn(sizeClass, className)}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
    />
  )
}
